/**
 * Bid legality — shared by the client (what the UI offers) and the server
 * (what it accepts), so the two can never drift into offering a bid that
 * would be silently dropped.
 */

export const REBID_RULES = ['anyone', 'everyoneExceptLeader', 'oneBidPerPlayer'];

/** The standing bid this player holds, or null. */
function standingBid(bids, playerId) {
  return (bids ?? []).find(b => b.playerId === playerId) ?? null;
}

/**
 * May this player still put a bid in? True when they have no standing bid;
 * otherwise it is the mode's rebid rule that decides. An unrecognised rule is
 * treated as the strictest one — better to refuse than to offer a bid the
 * server will drop.
 */
export function canRebid({ bids, playerId, rebidRule }) {
  if (!standingBid(bids, playerId)) return true;
  if (rebidRule === 'anyone') return true;
  if (rebidRule === 'everyoneExceptLeader') {
    // bids are kept sorted ascending on both sides, so [0] is the leader.
    return (bids ?? [])[0]?.playerId !== playerId;
  }
  return false;
}

/**
 * Is `count` a bid this player can actually place right now? A rebid has to be
 * permitted by the mode *and* improve on their own standing bid — a rebid you
 * cannot lose by is not a bid, it is a free re-roll.
 */
export function isBidLegal({ bids, playerId, rebidRule, count }) {
  if (!Number.isInteger(count)) return false;
  const standing = standingBid(bids, playerId);
  if (!standing) return true;
  if (!canRebid({ bids, playerId, rebidRule })) return false;
  return count < standing.count;
}
